/**
 * Global toast notification system.
 * Displays success/error/warning/info messages that auto-dismiss.
 *
 * Usage:
 *   import { showToast } from "./components/toast.ts";
 *   showToast({ type: "success", message: "Saved!" });
 *   showToast({ type: "error", message: "Something failed", duration: 8000 });
 */

import { html, nothing, type TemplateResult } from "lit";

export type ToastType = "success" | "error" | "warning" | "info";

export type ToastItem = {
  id: number;
  type: ToastType;
  message: string;
  duration: number;
  createdAt: number;
};

type ToastOptions = {
  type: ToastType;
  message: string;
  /** Auto-dismiss duration in ms. Default: 5000 (5s). Set 0 to keep until manually dismissed. */
  duration?: number;
};

let nextId = 1;
const toasts: ToastItem[] = [];
let renderCallback: (() => void) | null = null;

/** Register a callback that triggers a Lit re-render when toasts change. */
export function setToastRenderCallback(cb: () => void) {
  renderCallback = cb;
}

/** Show a toast notification. */
export function showToast(opts: ToastOptions): number {
  const id = nextId++;
  const duration = opts.duration ?? 5000;
  const toast: ToastItem = {
    id,
    type: opts.type,
    message: opts.message,
    duration,
    createdAt: Date.now(),
  };
  toasts.push(toast);
  if (toasts.length > 8) {
    toasts.shift();
  }
  renderCallback?.();
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  return id;
}

/** Dismiss a toast by id. */
export function dismissToast(id: number) {
  const idx = toasts.findIndex((t) => t.id === id);
  if (idx >= 0) {
    toasts.splice(idx, 1);
    renderCallback?.();
  }
}

/** Get icon SVG for toast type. */
function toastIcon(type: ToastType): TemplateResult {
  switch (type) {
    case "success":
      return html`
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M20 6L9 17l-5-5"></path>
        </svg>
      `;
    case "error":
      return html`
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      `;
    case "warning":
      return html`
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          ></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      `;
    case "info":
      return html`
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      `;
  }
}

/** Render the toast container. Call this inside the main app render. */
export function renderToasts(): TemplateResult {
  if (toasts.length === 0) return html`${nothing}`;

  return html`
    <div class="toast-container" aria-live="polite" aria-atomic="false">
      ${toasts.map(
        (toast) => html`
        <div class="toast toast--${toast.type}" role="alert">
          <span class="toast__icon">${toastIcon(toast.type)}</span>
          <span class="toast__message">${toast.message}</span>
          <button
            class="toast__dismiss"
            @click=${() => dismissToast(toast.id)}
            aria-label="Dismiss"
          >&times;</button>
        </div>
      `,
      )}
    </div>
  `;
}
